import { zodResolver } from '@hookform/resolvers/zod';
import { Radio, AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useEditSource } from '../hooks/useEditSource';
import { useSourceDetail } from '../hooks/useSourceDetail';
import { editSourceSchema, type EditSourceFormValues } from '../schemas/sources-schema';
import {
  NETWORK_STRING_TO_ENUM,
  STRING_TO_SOURCE_TYPE_MAP,
  type SourceTypeString,
} from '../types/sources.types';
import {
  CommunicatesFields,
  OpenMediaFields,
  SocialMediaFields,
  SubscriptionMediaFields,
} from './SourceTypeFields';
import { getErrorFromAxios } from '@/core/api/error-messages';
import { CountryHierarchyMultiSelect } from '@/features/countries/components/CountryHierarchyMultiSelect';
import { regionsApi } from '@/features/regions/api/regions.api';
import { RegionHierarchyMultiSelect } from '@/features/regions/components/RegionHierarchyMultiSelect';
import type { Region } from '@/features/regions/types/regions.types';
import type { RegionSelector } from '@/features/regions/types/regions.types';
import {
  RegionSelector as RegionSelectorEnum,
  RegionSelectorLabels,
} from '@/features/regions/types/regions.types';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface EditSourceModalProps {
  sourceId: string | undefined;
  open: boolean;
  onClose: () => void;
}

const SOURCE_TYPE_OPTIONS = [
  { label: 'Medios Abiertos', value: 'MediosAbiertos' },
  { label: 'Medios Suscripción', value: 'MediosSuscripcion' },
  { label: 'Redes Sociales', value: 'RedesSociales' },
  // { label: 'Tendencias - Google Trends', value: 'GoogleTrends' }, // TODO: not implemented yet
  { label: 'Comunicados', value: 'Comunicados' },
];

export function EditSourceModal({ sourceId, open, onClose }: EditSourceModalProps) {
  const { mutate, isPending } = useEditSource();
  // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
  const { source, isLoading } = useSourceDetail(open ? sourceId : undefined);
  const [pendingValues, setPendingValues] = useState<EditSourceFormValues | null>(null);
  const [hasReset, setHasReset] = useState(false);

  // Region metadata (countryIds + selector) needed to populate the region selector.
  // If the backend already returns these fields (after PR #233 is deployed), we use them directly.
  // Otherwise, we fetch the first region's details to get them (workaround until backend is updated).
  const [regionMeta, setRegionMeta] = useState<{
    countryIds: string[];
    selector: number;
  } | null>(null);

  useEffect(() => {
    if (!source || !open || source.regions.length === 0) {
      setRegionMeta(null);
      return;
    }

    const first = source.regions[0] as Region | undefined;
    // A source can span multiple countries across its regions, so aggregate the countryIds
    // of ALL regions. Relying on regions[0] alone hid the remaining countries in the selector.
    const allCountryIds = Array.from(new Set(source.regions.flatMap((r) => r.countryIds)));
    // The backend already returns countryIds and selector for each region, so we use them
    // directly. The getRegionById fallback below only guards against older data where these
    // fields are missing.
    if (
      first &&
      allCountryIds.length > 0 &&
      first.selector !== undefined &&
      first.selector !== null
    ) {
      setRegionMeta({ countryIds: allCountryIds, selector: first.selector });
      return;
    }

    // Backend doesn't return countryIds/selector yet — fetch region details to get them
    void regionsApi.getRegionById(first!.id).then((result) => {
      const region = result.data as Region | undefined;
      if (region && region.countryIds?.length && region.selector !== undefined) {
        setRegionMeta({ countryIds: region.countryIds, selector: region.selector });
      }
    });
  }, [source, open]);

  const form = useForm<EditSourceFormValues>({
    resolver: zodResolver(editSourceSchema),
    defaultValues: {
      name: '',
      type: 'MediosAbiertos',
      updateIntervalHours: 24,
      countryIds: undefined,
      selector: undefined,
      regionIds: [],
      isEnabled: true,
      baseUrl: '',
      username: '',
      password: '',
      keywords: [],
    },
    mode: 'onChange',
  });

  const sourceType = form.watch('type');
  const selectedCountryIds = form.watch('countryIds') ?? [];
  const selectedSelector = form.watch('selector');

  // Derived straight from the fetched detail — mirroring these into state caused a
  // prop-synced-in-effect bug and an unnecessary set-state cascade.
  const originalType = source?.type ?? null;
  const hasHistoricalData = source?.hasHistoricalData ?? false;

  // El selector de regiones solo se monta con hasReset, y hasReset exige que el reset ya
  // haya poblado pais y selector: se lee el formulario directo. Con un respaldo desde
  // regionMeta, vaciar la seleccion resucitaba el pais original y seguian listandose sus
  // regiones.
  const selectedCountryIdsKey = selectedCountryIds.join(',');
  useEffect(() => {
    if (!source || !open) {
      setHasReset(false);
      setRegionMeta(null);
      return;
    }

    const countryIds = regionMeta?.countryIds ?? [];
    const selector = regionMeta?.selector;

    form.reset({
      name: source.name,
      type: source.type,
      updateIntervalHours: source.updateIntervalHours,
      countryIds: countryIds.length > 0 ? countryIds : undefined,
      selector,
      regionIds: source.regions.map((r) => r.id),
      isEnabled: source.isEnabled,
      baseUrl: source.maskedUrl ?? '',
      username: '',
      password: '',
      network: source.network ? NETWORK_STRING_TO_ENUM[source.network] : undefined,
      keywords: source.keywords ?? [],
    });

    // Only mark hasReset when we have the region metadata needed to populate
    // the region selector. Otherwise the selector mounts with empty countryIds
    // and shows "Sin opciones".
    if (countryIds.length > 0 && selector !== undefined) {
      const timer = setTimeout(() => setHasReset(true), 0);
      return () => clearTimeout(timer);
    }
  }, [source, open, form, regionMeta]);
  const onSubmit = (values: EditSourceFormValues) => {
    if (!sourceId) return;

    const typeChanged = values.type !== originalType;

    if (typeChanged && hasHistoricalData) {
      setPendingValues(values);
      return;
    }

    executeUpdate(sourceId, values);
  };

  const executeUpdate = (id: string, values: EditSourceFormValues) => {
    const payload = {
      name: values.name,
      type: STRING_TO_SOURCE_TYPE_MAP[values.type],
      updateIntervalHours: values.updateIntervalHours,
      regionIds: values.regionIds,
      isEnabled: values.isEnabled,
      ...(values.baseUrl && { baseUrl: values.baseUrl }),
      ...(values.username && { username: values.username }),
      ...(values.password && { password: values.password }),
      ...(values.network !== undefined && { network: values.network }),
      ...(values.keywords && values.keywords.length > 0 && { keywords: values.keywords }),
    };

    mutate(
      { id, data: payload },
      {
        onSuccess: () => {
          form.reset();
          setPendingValues(null);
          onClose();
        },
        onError: (error) => {
          const errorInfo = getErrorFromAxios(error);
          const errorCode = (error as { response?: { data?: { title?: string } } })?.response?.data
            ?.title;

          if (errorCode === 'Source.NameAlreadyExists') {
            form.setError('name', { message: errorInfo.message });
          } else if (
            errorCode === 'Source.UrlAlreadyExists' ||
            errorCode === 'Source.Url.Invalid'
          ) {
            form.setError('baseUrl', { message: errorInfo.message });
          } else if (errorCode === 'Source.UpdateInterval.Invalid') {
            form.setError('updateIntervalHours', { message: errorInfo.message });
          } else if (
            errorCode === 'Source.RegionIds.Empty' ||
            errorCode === 'Source.RegionIds.Invalid'
          ) {
            form.setError('regionIds', { message: errorInfo.message });
          } else if (errorCode === 'Source.BaseUrl.Required') {
            form.setError('baseUrl', { message: errorInfo.message });
          } else if (errorCode === 'Source.Username.Required') {
            form.setError('username', { message: errorInfo.message });
          } else if (errorCode === 'Source.Password.Required') {
            form.setError('password', { message: errorInfo.message });
          } else if (errorCode === 'Source.Network.Required') {
            form.setError('network', { message: errorInfo.message });
          } else if (errorCode === 'Source.Keywords.Empty') {
            form.setError('keywords', { message: errorInfo.message });
          } else {
            Notification.error({ title: errorInfo.title, description: errorInfo.message });
          }
        },
      },
    );
  };

  const handleConfirm = () => {
    if (!pendingValues || !sourceId) return;
    executeUpdate(sourceId, pendingValues);
  };

  const handleClose = () => {
    if (!isPending) {
      setPendingValues(null);
      onClose();
    }
  };

  const handleTypeChange = (value: string) => {
    form.setValue('type', value as SourceTypeString, { shouldValidate: true });
    form.setValue('baseUrl', '');
    form.setValue('username', '');
    form.setValue('password', '');
    form.setValue('network', undefined);
    form.setValue('keywords', []);
  };

  const getSelectedTypeLabel = () => {
    return SOURCE_TYPE_OPTIONS.find((o) => o.value === sourceType)?.label || '';
  };

  const typeChanged = sourceType !== originalType;

  if (isLoading) {
    return (
      <ModalForm
        open={open}
        onOpenChange={handleClose}
        title="Editar fuente de información"
        className="sm:max-w-[600px]"
      >
        <div className="py-8" aria-hidden />
      </ModalForm>
    );
  }

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<Radio />}
        onOpenChange={handleClose}
        title="Editar fuente de información"
        description="Modifica la configuración de la fuente seleccionada."
        className="sm:max-w-[600px]"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
                data-testid="edit-source-cancel-btn"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isPending}
                data-testid="edit-source-submit-btn"
              >
                Guardar cambios
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem]">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Nombre de la fuente"
                    placeholder="Ingresar nombre"
                    autoComplete="off"
                    {...field}
                    disabled={isPending}
                    error={form.formState.errors.name?.message}
                    data-testid="edit-source-name-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <SelectInput
                    key={sourceType}
                    value={field.value ?? 'MediosAbiertos'}
                    label="Tipo de fuente"
                    onValueChange={handleTypeChange}
                    disabled={isPending}
                    placeholder="Seleccionar tipo"
                    error={form.formState.errors.type?.message}
                    data-testid="edit-source-type-select"
                  >
                    {SOURCE_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectInput>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="updateIntervalHours"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    label="Intervalo de actualización (horas)"
                    placeholder="24"
                    type="number"
                    min={1}
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                    disabled={isPending}
                    error={form.formState.errors.updateIntervalHours?.message}
                    data-testid="edit-source-interval-input"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* País, tipo de región y selector de regiones */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[1.23rem] mt-[1.23rem]">
          <FormField
            control={form.control}
            name="countryIds"
            render={({ field: countryField }) => (
              <FormItem>
                <FormControl>
                  <CountryHierarchyMultiSelect
                    label="País / Territorio"
                    placeholder="Seleccionar país"
                    value={countryField.value ?? []}
                    onValueChange={(ids) => {
                      countryField.onChange(ids);
                      form.setValue('regionIds', []);
                    }}
                    disabled={isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="selector"
            render={({ field: selectorField }) => (
              <FormItem>
                <FormControl>
                  <SelectInput
                    label="Tipo de región"
                    value={selectorField.value?.toString() ?? ''}
                    onValueChange={(value) => {
                      // Radix Select fires onValueChange("") from its hidden native <select>
                      // (SelectBubbleInput) when the controlled value changes before the native
                      // options are mounted. Number("") === 0, which would corrupt the field to
                      // PoliticaElectoral. Ignore the empty string.
                      if (value === '') return;
                      selectorField.onChange(Number(value));
                      form.setValue('regionIds', []);
                    }}
                    disabled={isPending}
                    placeholder="Seleccionar tipo"
                  >
                    {Object.entries(RegionSelectorEnum).map(([key, value]) => (
                      <SelectItem key={key} value={value.toString()}>
                        {RegionSelectorLabels[value as RegionSelector]}
                      </SelectItem>
                    ))}
                  </SelectInput>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {hasReset && selectedCountryIds.length > 0 && selectedSelector !== undefined && (
          <div className="mt-[1.23rem]">
            <FormField
              control={form.control}
              name="regionIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RegionHierarchyMultiSelect
                      key={`${selectedCountryIdsKey}-${selectedSelector}`}
                      countryIds={selectedCountryIds}
                      selector={selectedSelector as RegionSelector}
                      value={field.value}
                      onValueChange={field.onChange}
                      label="Regiones asociadas"
                      placeholder="Seleccionar regiones"
                      disabled={isPending}
                      error={form.formState.errors.regionIds?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-outline">
          <FormField
            control={form.control}
            name="isEnabled"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isPending}
                    data-testid="edit-source-enabled-switch"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <label className="text-sm text-primary font-medium">
            {form.watch('isEnabled') ? 'Fuente habilitada' : 'Fuente deshabilitada'}
          </label>
        </div>

        {typeChanged && (
          <div className="flex items-start gap-3 mt-4 p-3 rounded-lg bg-warn/10 border border-warn/30">
            <AlertTriangle className="w-5 h-5 text-warn flex-shrink-0 mt-0.5" />
            <p className="text-sm text-primary">
              Cambiar el tipo de fuente puede afectar datos históricos existentes.
            </p>
          </div>
        )}

        {sourceType === 'MediosAbiertos' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <OpenMediaFields />
          </div>
        )}
        {sourceType === 'MediosSuscripcion' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <SubscriptionMediaFields />
          </div>
        )}
        {sourceType === 'RedesSociales' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <SocialMediaFields />
          </div>
        )}
        {/* TODO: not implemented yet
        {sourceType === 'GoogleTrends' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <GoogleTrendsFields />
          </div>
        )}
        */}
        {sourceType === 'Comunicados' && (
          <div className="mt-6 pt-6 border-t border-outline">
            <CommunicatesFields />
          </div>
        )}
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isPending && !v && setPendingValues(null)}
        title="Advertencia: Cambio de tipo de fuente"
        description={
          <div className="flex flex-col gap-2">
            <p>
              Cambiar el tipo de fuente puede afectar datos históricos existentes. ¿Deseas
              continuar?
            </p>
            <p className="text-sm text-primary/70">
              Se actualizará de{' '}
              <span className="font-medium text-light">
                {SOURCE_TYPE_OPTIONS.find((o) => o.value === originalType)?.label}
              </span>{' '}
              a <span className="font-medium text-light">{getSelectedTypeLabel()}</span>.
            </p>
          </div>
        }
        onConfirm={handleConfirm}
        confirmLabel="Sí, continuar"
        confirmLoadingLabel="Guardando..."
        isPending={isPending}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
